/**
 * MobileMyCaptures — "My Captures": the caller's own recordings/memos/notes, with a live processing
 * status pill, so a user can find and re-open what they've captured without digging through Inbox.
 *
 * Route: /partner/m/captures
 *
 * RLS DECISION (LOCKED, see task brief) — research_sources read stays TEAM-GATED (can_read_research);
 * there is no owner-scoped RLS or RPC for this list. This screen relies on that team-wide read and
 * simply CLIENT-FILTERS to `.eq('ingested_by', <auth uid>)` so only the caller's own captures show —
 * every recording user is Investment team, so the team-gated read already covers them. Do not treat the
 * `.eq()` as a security boundary; it is a display filter on top of RLS the caller already has.
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly (MobileInbox / MobileResearch):
 *   • ONE useCachedQuery resolves the whole list as a unit and write-throughs it under a per-user
 *     cacheKey (`mobile.my_captures.<uid>`) so a shared device never bleeds one user's captures into
 *     another's cached placeholder.
 *   • The queryFn THROWS on a hard `{ error }` so the cached last-known-good keeps serving offline
 *     instead of being overwritten with a partial/empty value.
 *   • A stable EMPTY array guarantees the screen is never blank on an offline cold start.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *
 * STATUS PILL — each row's status starts from research_sources.status/status_detail (already returned
 * by the list read), then is refreshed via the existing owner-scoped `getCaptureStatus` wrapper
 * (capture_status_summary RPC — the same one CaptureSheet polls with while a capture is in flight) for
 * the freshest live state. The RPC call is best-effort: if it fails, the row falls back to the columns
 * from the list read rather than failing the whole screen (a stale-but-present pill beats a blank list).
 *
 * The generated supabase types are stale for some of these shapes, so `.from()`/`.rpc()` calls are cast
 * through `any` (matching the sibling cluster screens). No writes. Web/desktop are untouched (this is a
 * /partner/m/* surface).
 */
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mic,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { getCaptureStatus } from '@/lib/capture';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

interface CaptureRow {
  id: string;
  title: string | null;
  createdAt: string | null;
  status: string;
  statusDetail: string | null;
}

const EMPTY_LIST: CaptureRow[] = [];

// Terminal/ready states from research_source_status — everything else in the enum is an in-flight
// processing stage (queued/fetching/extracting/transcribing/cleaning_audio/chunking/context_blurbing/
// embedding/enriching/generating_questions/uploading/cleaning_audio_blocked).
const FAILED_STATUSES = new Set(['failed', 'blocked_consent', 'cleaning_audio_blocked', 'suppressed']);
const READY_STATUSES = new Set(['ready', 'duplicate']);

async function fetchMyCaptures(userId: string): Promise<CaptureRow[]> {
  // Team-gated read (can_read_research RLS) + client-filter to the caller's own captures — see the
  // RLS DECISION note at the top of this file. Newest first, capped so the screen stays fast.
  const { data, error } = await (supabase.from('research_sources') as any)
    .select('id, title, created_at, status, status_detail')
    .eq('ingested_by', userId)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;

  const rows = (data ?? []) as Array<{
    id: string;
    title: string | null;
    created_at: string | null;
    status: string | null;
    status_detail: string | null;
  }>;

  const base: CaptureRow[] = rows.map((r) => ({
    id: String(r.id),
    title: r.title,
    createdAt: r.created_at,
    status: String(r.status ?? 'queued'),
    statusDetail: r.status_detail,
  }));

  // Best-effort live-status overlay via the existing owner-scoped RPC. A failure here must not blank
  // the whole list — fall back to the columns already read above.
  try {
    const ids = base.map((r) => r.id);
    const live = await getCaptureStatus(ids);
    const byId = new Map(live.map((s) => [s.source_id, s]));
    return base.map((r) => {
      const l = byId.get(r.id);
      return l ? { ...r, status: l.status, statusDetail: l.status_detail } : r;
    });
  } catch {
    return base;
  }
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

// Readable label for a raw status/status_detail token — Title Case fallback so an unmapped value is
// still legible rather than raw snake_case.
function prettyStatus(token: string): string {
  return token
    .split(/[_\-\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// RAG by icon + text (never colour-only, matching MobileInbox's severityMeta pattern).
function statusMeta(row: CaptureRow): { label: string; Icon: typeof Loader2; tone: string; spin?: boolean } {
  const label = prettyStatus(row.statusDetail || row.status);
  if (FAILED_STATUSES.has(row.status)) {
    return { label, Icon: AlertTriangle, tone: 'text-red-600' };
  }
  if (READY_STATUSES.has(row.status)) {
    return { label: row.status === 'ready' ? 'Ready' : label, Icon: CheckCircle2, tone: 'text-green-600' };
  }
  return { label, Icon: Loader2, tone: 'text-amber-600', spin: true };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileMyCaptures() {
  const online = useIsOnline();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const captures = useCachedQuery<CaptureRow[]>({
    queryKey: ['mobile-my-captures', userId],
    cacheKey: `mobile.my_captures.${userId ?? 'anon'}`,
    queryFn: () => fetchMyCaptures(userId as string),
    enabled: !!userId,
  });

  const items = captures.data ?? EMPTY_LIST;

  const showStale = captures.isFromCache && captures.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && captures.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(captures.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // Hard error only when there's nothing cached to fall back to.
  const hardError = captures.isError && !captures.isFromCache && captures.data === undefined;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <h1 className="font-heading text-2xl font-semibold text-primary">My Captures</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {items.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {items.length} capture{items.length === 1 ? '' : 's'}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load your captures just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved copy of your captures yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : items.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Mic className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">No captures yet</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Record a meeting, jot a voice memo or drop a link — your captures will appear here with
              their processing status.
            </p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const when = formatRelative(item.createdAt);
              const meta = statusMeta(item);
              return (
                <li key={item.id}>
                  <Link
                    to={`/partner/m/source/${encodeURIComponent(item.id)}`}
                    className="block rounded-xl border border-border bg-card p-4 transition-colors active:bg-muted/50"
                    aria-label={`Open capture: ${item.title ?? 'Untitled capture'}`}
                  >
                    <div className="flex items-start gap-2">
                      <h2 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold">
                        {item.title ?? 'Untitled capture'}
                      </h2>
                      <ChevronRight
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={cn('inline-flex items-center gap-1 text-[11px] font-semibold', meta.tone)}>
                        <meta.Icon className={cn('h-3 w-3', meta.spin && 'animate-spin')} aria-hidden="true" />
                        {meta.label}
                      </span>
                      {when && (
                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                          {when}
                        </span>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
